import { PlatformError } from "@/lib/api/client";
import { messageFor } from "@/lib/api/messages";
import type { paths } from "@/lib/api/schema";
import { formatDecimal, withCurrency } from "@/lib/format/amount";

/* Everything a race board shows, derived from what the platform states. Pure
   functions, shared by the server render and the browser's polling, so the
   first frame and every later one are formatted the same way. */

type PromotionListItem =
  paths["/promotions"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

export type PromotionRace = NonNullable<PromotionListItem["race"]>;
export type RaceKind = PromotionRace["kind"];
export type RaceProduct = PromotionRace["products"][number];

export type RaceEntry =
  paths["/leaderboards/races/{promotionRaceId}"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

export type QualifiedBetsPage =
  paths["/leaderboards/races/{promotionRaceId}/qualified-bets"]["get"]["responses"]["200"]["content"]["application/json"];

export type QualifiedBet = QualifiedBetsPage["data"][number];

export type VolumeEntry =
  paths["/leaderboards/volume"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

/* The race's own token decides how its money reads — the symbol, and how many
   decimals matter. Race amounts arrive as decimal strings ("1089.39"), not the
   base units the rest of the API uses. */
export type RaceToken = { symbol: string; significantDecimals: number };

/* Ten to a page, as the whitelabel pages them. */
export const QUALIFIED_PAGE_SIZE = 10;

/* What to tell the reader when a board's query fails. The platform's own
   failures carry a status the messages know how to phrase; anything else —
   a dropped connection, a parse error — gets one plain line. */
export const boardError = (error: unknown) =>
  error instanceof PlatformError
    ? messageFor(error, "leaderboard")
    : "The leaderboard didn’t answer. It will try again on its own in a moment.";

export const RACE_VALUE_LABEL: Record<RaceKind, string> = {
  wagering: "WAGERED",
  bets: "BETS",
  "highest-multiplier": "MULTIPLIER",
};

export const PRODUCT_LABEL: Record<RaceProduct, string> = {
  casino: "Casino",
  sportsbook: "Sportsbook",
};

export const money = (value: string, token: RaceToken | null) =>
  token ? withCurrency(formatDecimal(value, token.significantDecimals), token.symbol) : formatDecimal(value, 2);

export const formatMultiplierValue = (value: number) => `${value.toFixed(2)}x`;

/* What ranks a player, by the race's kind: total wagered, number of bets, or
   the single highest multiplier. */
export const raceMetricValue = (entry: RaceEntry, token: RaceToken | null) => {
  if (entry.kind === "wagering") {
    return money(entry.wagered, token);
  }

  if (entry.kind === "bets") {
    return formatDecimal(String(entry.bets), 0);
  }

  return formatMultiplierValue(entry.highestMultiplier);
};

export const ordinal = (n: number) => {
  const tens = n % 100;

  if (tens >= 11 && tens <= 13) {
    return `${n}th`;
  }

  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

/* "1d 04:12:33" for a race that runs for months, "00:00:15" in its last
   seconds — the kit's "Ends in" badge counts down in clock digits. */
export const formatCountdown = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  const clock = [Math.floor((total % 86400) / 3600), Math.floor((total % 3600) / 60), total % 60]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");

  return days > 0 ? `${days}d ${clock}` : clock;
};

export const raceClock = (now: number, race: { from: string; to: string; status: string }) => {
  const from = Date.parse(race.from);
  const to = Date.parse(race.to);

  if (race.status === "completed" || (Number.isFinite(to) && now >= to)) {
    return "Ended";
  }

  if (race.status === "scheduled" || (Number.isFinite(from) && now < from)) {
    return `Starts in ${formatCountdown(from - now)}`;
  }

  return Number.isFinite(to) ? `Ends in ${formatCountdown(to - now)}` : "Live";
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const twoDigits = (value: number) => String(value).padStart(2, "0");

/* "28 Aug, 12:54", as the kit's qualified-bets row prints a time, in the
   visitor's own clock. Built from parts rather than a locale: en-GB's short
   month is "Sept", which breaks the column's rhythm every September. */
export const qualifiedBetTime = (iso: string) => {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return `${twoDigits(date.getDate())} ${MONTHS[date.getMonth()]}, ${twoDigits(date.getHours())}:${twoDigits(date.getMinutes())}`;
};

type Betslip = Extract<QualifiedBet, { product: "sportsbook" }>["betslip"];

const betslipLabel = (betslip: Betslip) => {
  const legs = betslip.bets
    .map((leg) => leg.competitors?.filter(Boolean).join(" v ") || leg.selection || leg.market || "")
    .filter((leg) => leg.length > 0);

  if (legs.length === 0) {
    return betslip.betType || "Sportsbook";
  }

  return legs.length === 1 ? legs[0] : `${legs[0]} +${legs.length - 1}`;
};

/* The thing the bet was on: a game the row can link to, or a betslip named by
   its legs. */
export const qualifiedBetEvent = (bet: QualifiedBet) =>
  bet.product === "casino"
    ? { kind: "game" as const, name: bet.game.name, slug: bet.game.slug }
    : { kind: "betslip" as const, name: betslipLabel(bet.betslip) };

/* The prize each rank takes: the platform states the pool and each rank's
   share of it. */
export const payoutTable = (race: {
  payouts: { rank: number; percentage: number }[];
  prizePool: string;
  token: RaceToken;
}) =>
  [...race.payouts]
    .sort((left, right) => left.rank - right.rank)
    .map((payout) => ({
      rank: payout.rank,
      shareLabel: `${Math.round(payout.percentage * 1000) / 10}%`,
      prize: (Number(race.prizePool) * payout.percentage).toFixed(race.token.significantDecimals),
    }));
